import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Calendar, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Side { id: string; code: string; nameEn: string }

interface SalaryRun {
  id: string; driver_id: string; driver_name: string | null; period_month: string;
  side_code: string | null; side_name: string | null;
  iqama: string | null; vehicle_plates: string | null;
  basic_salary_sar: string; total_incentives_released_sar: string; total_advances_sar: string;
  total_reimbursables_sar: string; total_deductions_sar: string;
  net_payable_sar: string; status: string;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ymNow(offset = 0): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 7);
}

export default function MonthlySalaryRunsPage() {
  const [period, setPeriod] = useState(ymNow(-1));
  // '' = every side. 'none' = the drivers filed under no side at all.
  const [side, setSide] = useState('');
  // What is typed, and what has been asked for. They differ for 300ms so a fast
  // typist does not fire a query per keystroke and get answers out of order.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [sides, setSides] = useState<Side[]>([]);
  const [rows, setRows] = useState<SalaryRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ period });
      if (side) qs.set('side', side);
      if (query) qs.set('search', query);
      const r = await apiClient.get<SalaryRun[]>('/api/v1/driver-finance/salary-runs?' + qs.toString());
      setRows(r.data);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [period, side, query]);

  useEffect(() => { refresh(); }, [refresh]);

  // Debounce: the request follows what was typed, 300ms behind.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // The sides come from the server rather than a hard-coded list, so a new one
  // appears here the day it is created instead of the day someone remembers.
  useEffect(() => {
    apiClient.get<{ items: Array<{ id: string; code: string; nameEn?: string; name_en?: string }> }>('/api/v1/projects')
      .then((r) => setSides((r.data.items ?? []).map((p) => ({
        id: p.id, code: p.code, nameEn: p.nameEn ?? p.name_en ?? p.code,
      }))))
      .catch(() => setSides([]));   // the filter is optional; the page is not
  }, []);

  async function generateRuns() {
    // PR MAY10 P4A.7: validate period before submit so user gets inline
    // feedback instead of the Zod error from the API.
    if (!/^\d{4}-\d{2}$/.test(period)) {
      window.alert('Pick a month (YYYY-MM) before generating.');
      return;
    }
    setBusy(true);
    try {
      await apiClient.post('/api/v1/driver-finance/salary-runs/generate', { periodMonth: period });
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function approve(id: string) {
    setBusy(true);
    try { await apiClient.post('/api/v1/driver-finance/salary-runs/' + id + '/approve', {}); await refresh(); }
    catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  async function markPaid(id: string) {
    setBusy(true);
    try { await apiClient.post('/api/v1/driver-finance/salary-runs/' + id + '/mark-paid', {}); await refresh(); }
    catch (e) { window.alert(extractErrorMessage(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Calendar className="h-4 w-4" /> Monthly salary runs</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 py-4 space-y-4">
        <div className="flex items-center gap-2">
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="rounded border border-border bg-background px-3 py-2 text-sm" />
          {/* Beside the month: together they are one question - "NEOM, for
              July" - and apart they read as two. */}
          <select value={side} onChange={(e) => setSide(e.target.value)}
            aria-label="Filter by side"
            className="rounded border border-border bg-background px-3 py-2 text-sm">
            <option value="">All sides</option>
            {sides.map((sd) => <option key={sd.id} value={sd.code}>{sd.nameEn}</option>)}
            {/* Drivers nobody has filed. Folded into All they are invisible;
                named, they can be found and fixed. */}
            <option value="none">— no side —</option>
          </select>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, iqama or plate…"
            aria-label="Search salary runs"
            className="rounded border border-border bg-background px-3 py-2 text-sm w-56"
          />
          {/* Generates the month for everyone on purpose. A button that quietly
              did less because a filter was set is how payroll comes up short. */}
          <Button variant="gold" onClick={generateRuns} disabled={busy} className="gap-2">Generate / refresh runs</Button>
        </div>

        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <PageError error={error} retry={refresh} />
        : rows.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
            {query
              ? <>Nothing matches “{query}” for {period}
                  {side ? ` on ${sides.find((sd) => sd.code === side)?.nameEn ?? side}` : ''}.{' '}
                  <button type="button" className="underline" onClick={() => setSearch('')}>Clear the search</button></>
              : side
              // Naming the filter matters: "no runs, click Generate" under an
              // active side filter sends someone to generate runs that already
              // exist on another side.
              ? <>No runs for {period} on {sides.find((sd) => sd.code === side)?.nameEn ?? (side === 'none' ? 'drivers with no side' : side)}.{' '}
                  <button type="button" className="underline" onClick={() => setSide('')}>Show all sides</button></>
              : <>No runs for {period}. Click Generate.</>}
          </div>
        )
        : <div className="rounded-lg border border-border overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground"><tr>
                <th className="text-right px-3 py-2 font-normal w-10">#</th>
                <th className="text-left px-3 py-2 font-normal whitespace-nowrap">Driver</th>
                <th className="text-left px-3 py-2 font-normal whitespace-nowrap">Iqama</th>
                <th className="text-left px-3 py-2 font-normal whitespace-nowrap" title="The truck this driver is assigned to now — not the one held during this month">Vehicle now</th>
                <th className="text-left px-3 py-2 font-normal whitespace-nowrap">Side</th>
                <th className="text-right px-3 py-2 font-normal whitespace-nowrap">Basic</th>
                <th className="text-right px-3 py-2 font-normal whitespace-nowrap">Incentives</th>
                <th className="text-right px-3 py-2 font-normal whitespace-nowrap">Advances</th>
                <th className="text-right px-3 py-2 font-normal whitespace-nowrap">Reimb</th>
                <th className="text-right px-3 py-2 font-normal whitespace-nowrap">Net</th>
                <th className="text-left px-3 py-2 font-normal whitespace-nowrap">Status</th>
                <th className="text-right px-3 py-2 font-normal whitespace-nowrap">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-t border-border">
                    {/* Numbers what is on screen, so it restarts at 1 when the
                        month or the side changes. */}
                    <td className="px-3 py-2 text-right text-muted-foreground font-mono tabular-nums">{i + 1}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.driver_name ?? r.driver_id.slice(0, 8)}</td>
                    {/* Beside the name, because it is the thing that tells two
                        identical names apart. */}
                    <td className="px-3 py-2 font-mono text-muted-foreground whitespace-nowrap">
                      {r.iqama ?? '—'}
                    </td>
                    {/* The truck assigned right now, so the same on every
                        month. Deliberate while the fleet is still being set up
                        and reassignments are configuration, not history. */}
                    <td className="px-3 py-2 font-mono whitespace-nowrap">
                      {r.vehicle_plates ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    {/* Shown, not just filtered on: a list that narrows without
                        saying what survived asks you to take it on trust. */}
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                      {r.side_name ?? <span className="text-amber-600">no side</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{fmtSar(Number(r.basic_salary_sar))}</td>
                    <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{fmtSar(Number(r.total_incentives_released_sar))}</td>
                    <td className="px-3 py-2 text-right font-mono text-rose-700 dark:text-rose-400 whitespace-nowrap">−{fmtSar(Number(r.total_advances_sar))}</td>
                    <td className="px-3 py-2 text-right font-mono whitespace-nowrap">{fmtSar(Number(r.total_reimbursables_sar))}</td>
                    <td className="px-3 py-2 text-right font-mono font-medium whitespace-nowrap">{fmtSar(Number(r.net_payable_sar))}</td>
                    <td className="px-3 py-2 whitespace-nowrap"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-muted/40 border border-border">{r.status}</span></td>
                    <td className="px-3 py-2 text-right">
                      {r.status === 'draft' && <Button size="sm" variant="outline" onClick={() => approve(r.id)} disabled={busy} className="gap-1"><Check className="h-3 w-3" /> Approve</Button>}
                      {r.status === 'approved' && <Button size="sm" variant="gold" onClick={() => markPaid(r.id)} disabled={busy}>Mark paid</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </main>
    </div>
  );
}
